import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageShell from '../Components/ui/PageShell';
import DashboardCard from '../Components/ui/DashboardCard';
import DashboardDateFilter from '../Components/ui/DashboardDateFilter';
import { useLayout } from '../layout/AppLayout';
import { getDecryptedData } from '../utils/authStorage';
import { getHomeReqData } from '../services/requirementService';
import { getRecruiterWorkLog } from '../services/workflowService';
import { getDefaultDashboardDateRange, todayDateInputValue } from '../utils/requirementDateFilter';
import { aggregateDailyProfileUploads } from '../utils/recruiterDashboardCharts';
import { computeRecruiterDashboardStats } from '../utils/dashboardStats';
import ProfilesSourcedBarChartCard from '../Components/analytics/ProfilesSourcedBarChartCard';

const DEFAULT_DATE_RANGE = getDefaultDashboardDateRange();

function RecruiterDashboard() {
  const userId = getDecryptedData('Id');
  const { tableRefresh } = useLayout();
  const [dateRange, setDateRange] = useState(DEFAULT_DATE_RANGE);
  const [requirements, setRequirements] = useState([]);
  const [workLogProfiles, setWorkLogProfiles] = useState([]);
  const [todayProfiles, setTodayProfiles] = useState(0);
  const [todayRequirements, setTodayRequirements] = useState(0);

  useEffect(() => {
    if (!userId) return;

    const load = async () => {
      try {
        const reqData = await getHomeReqData(userId);
        setRequirements(Array.isArray(reqData) ? reqData : []);

        const today = todayDateInputValue();
        const [rangeWorkLog, todayWorkLog] = await Promise.all([
          getRecruiterWorkLog(userId, dateRange.from, dateRange.to),
          getRecruiterWorkLog(userId, today, today),
        ]);

        setWorkLogProfiles(rangeWorkLog?.profiles || []);
        setTodayProfiles(todayWorkLog?.summary?.profilesSubmitted || 0);
        setTodayRequirements(todayWorkLog?.summary?.requirementsWorked || 0);
      } catch (error) {
        console.error('Recruiter dashboard load failed:', error);
        setRequirements([]);
        setWorkLogProfiles([]);
        setTodayProfiles(0);
        setTodayRequirements(0);
      }
    };

    load();
  }, [userId, dateRange.from, dateRange.to, tableRefresh.candidates, tableRefresh.requirements]);

  const stats = useMemo(
    () => computeRecruiterDashboardStats(requirements, workLogProfiles, dateRange),
    [requirements, workLogProfiles, dateRange],
  );

  const dailyUploads = useMemo(
    () => aggregateDailyProfileUploads(workLogProfiles),
    [workLogProfiles],
  );

  const chartRangeLabel = useMemo(() => {
    const fromLabel = dateRange.from
      ? new Date(`${dateRange.from}T12:00:00`).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
      : '';
    const toLabel = dateRange.to
      ? new Date(`${dateRange.to}T12:00:00`).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
      : '';
    return `${fromLabel} – ${toLabel}`;
  }, [dateRange.from, dateRange.to]);

  return (
    <PageShell
      title="Recruiter Dashboard"
      subtitle="Open My Workbench for requirements, uploads, and candidate status updates."
      breadcrumbs={[{ label: 'Dashboard', path: '/dashboard' }]}
    >
      <DashboardDateFilter
        onApply={setDateRange}
        onReset={setDateRange}
      />

      <p className="hr-dashboard-filter-hint">
        Showing:
        {dateRange.from ? ` ${dateRange.from}` : ''}
        {dateRange.to ? ` → ${dateRange.to}` : ''}
        {' · '}
        requirements by uploaded date, profiles by upload date
      </p>

      <div className="hr-stat-grid">
        <DashboardCard title="Today's Work" value={todayProfiles} subtitle={`${todayRequirements} requirement(s) worked today`} icon="📝" accent="purple" to="/work/my-today" />
        <DashboardCard title="Assigned Requirements" value={stats.requirements} icon="📋" accent="blue" to="/home?tab=claimed" />
        <DashboardCard title="Source Profiles Uploaded" value={stats.uploaded} icon="⬆️" accent="teal" to="/work/my-sourced-profiles" />
        <DashboardCard title="Interviews" value={stats.interviews} icon="🎯" accent="orange" to="/work/interviews" />
        <DashboardCard title="Offers" value={stats.offers} icon="📨" accent="green" />
        <DashboardCard title="Pending Follow-ups" value={stats.followUps} icon="⏳" accent="gray" to="/home?tab=claimed" />
      </div>

      <section className="hr-card hr-recruiter-upload-chart">
        <ProfilesSourcedBarChartCard
          title="Daily Profiles Uploaded"
          subtitle={chartRangeLabel}
          items={dailyUploads}
          axisLabel="Profiles Uploaded"
          sortBy="chronological"
          maxVisible={31}
          emptyMessage="No profiles uploaded for the selected date range."
        />
      </section>

      <section className="hr-card">
        <h3 className="hr-section-title">Quick Actions</h3>
        <div className="hr-dashboard-grid">
          <Link to="/work/my-today" className="hr-dashboard-card">
            <h3>Today&apos;s Work</h3>
            <p>View profiles submitted and requirements you worked on</p>
          </Link>
          <Link to="/home?tab=claimed" className="hr-dashboard-card">
            <h3>My Workbench</h3>
            <p>Requirements, uploads, and candidate actions in one place</p>
          </Link>
          <Link to="/work/interviews" className="hr-dashboard-card">
            <h3>Interview Schedule</h3>
            <p>View upcoming interviews and scheduled times</p>
          </Link>
          <Link to="/Profile" className="hr-dashboard-card">
            <h3>My Profile</h3>
            <p>View and update your profile</p>
          </Link>
        </div>
      </section>
    </PageShell>
  );
}

export default RecruiterDashboard;
